import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  CheckCircle2,
  KeyRound,
  ShieldCheck,
  ExternalLink,
  Trash2,
  Loader2,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

export default function LLMConfig() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({}); // {provider_id: {api_key, selected_model}}
  const [saving, setSaving] = useState({});
  const [removeTarget, setRemoveTarget] = useState(null);

  const load = async () => {
    try {
      const data = await api.llm.list();
      setConfigs(data || []);
      // Seed model selection from server values
      const seed = {};
      (data || []).forEach((c) => {
        seed[c.provider_id] = { api_key: "", selected_model: c.selected_model };
      });
      setEditing(seed);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setField = (id, field, value) => {
    setEditing((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }));
  };

  const save = async (cfg) => {
    const state = editing[cfg.provider_id] || {};
    if (!state.api_key || !state.api_key.trim()) {
      toast.error("API key required", {
        description: `Paste your ${cfg.name} key before saving.`,
      });
      return;
    }
    setSaving((p) => ({ ...p, [cfg.provider_id]: true }));
    try {
      const res = await api.llm.save(cfg.provider_id, state.api_key.trim(), state.selected_model);
      toast.success(`${cfg.name} key saved`, { description: res.message });
      setEditing((p) => ({ ...p, [cfg.provider_id]: { ...p[cfg.provider_id], api_key: "" } }));
      load();
    } catch (e) {
      toast.error("Save failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving((p) => ({ ...p, [cfg.provider_id]: false }));
    }
  };

  const remove = async (cfg) => {
    try {
      await api.llm.remove(cfg.provider_id);
      toast.success(`${cfg.name} key removed`);
      load();
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setRemoveTarget(null);
    }
  };

  if (loading) {
    return (
      <div className="px-6 lg:px-10 py-8 text-muted-foreground" data-testid="llm-config-loading">
        Loading LLM configuration…
      </div>
    );
  }

  return (
    <div className="px-6 lg:px-10 py-8 space-y-6" data-testid="llm-config-page">
      <div className="cs-card p-5 flex gap-3 items-start" data-testid="llm-config-banner">
        <ShieldCheck className="w-5 h-5 text-[#15803d] mt-0.5 shrink-0" />
        <div className="text-sm leading-relaxed flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="font-semibold text-foreground">Bring your own LLM keys</div>
            <button
              onClick={() => { load(); toast.success("Refreshed"); }}
              data-testid="llm-refresh"
              className="inline-flex items-center gap-1.5 px-3 h-7 rounded-full border border-border hover:border-[#00579e] hover:text-[#00579e] transition-colors text-[11px] font-medium"
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
          <p className="text-muted-foreground text-xs mt-2">
            <span className="font-semibold text-foreground">Heaviest usage:</span> see
            TokenMind → <span className="cs-mono text-[#00579e]">Provider breakdown</span> for
            which key is driving the bulk of tokens & cost this month.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {configs.map((cfg) => {
          const state = editing[cfg.provider_id] || {};
          const isSaving = !!saving[cfg.provider_id];
          return (
            <div
              key={cfg.provider_id}
              className="cs-card p-5 flex flex-col"
              data-testid={`llm-card-${cfg.provider_id}`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                  style={{ background: cfg.color }}
                >
                  {cfg.name
                    .split(" ")
                    .map((w) => w[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="cs-heading font-semibold text-sm tracking-tight">{cfg.name}</h3>
                  <a
                    href={cfg.doc_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[11px] text-[#00579e] hover:underline inline-flex items-center gap-0.5 mt-0.5"
                    data-testid={`llm-doc-link-${cfg.provider_id}`}
                  >
                    Get a key <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                {cfg.configured && (
                  <span
                    className="cs-pill bg-[#dcfce7] text-[#15803d]"
                    data-testid={`llm-status-${cfg.provider_id}`}
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    Configured
                  </span>
                )}
              </div>

              {cfg.configured ? (
                <div className="mb-3 p-2.5 rounded-lg bg-[hsl(var(--cs-tint))] border border-border">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                    Active key
                  </div>
                  <div
                    className="cs-mono text-xs mt-1 break-all"
                    data-testid={`llm-masked-${cfg.provider_id}`}
                  >
                    {cfg.masked_key}
                  </div>
                </div>
              ) : (
                <div className="mb-3 p-2.5 rounded-lg bg-[#fff7ed] border border-[#f59e0b]/30 flex items-center gap-2">
                  <AlertCircle className="w-3.5 h-3.5 text-[#9a3412]" />
                  <span className="text-xs text-[#9a3412]">No key configured yet</span>
                </div>
              )}

              <label className="text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground mb-1">
                API Key
              </label>
              <Input
                type="password"
                value={state.api_key || ""}
                onChange={(e) => setField(cfg.provider_id, "api_key", e.target.value)}
                placeholder={`${cfg.key_prefix}…`}
                data-testid={`llm-input-${cfg.provider_id}`}
                className="rounded-lg cs-mono text-xs"
              />

              <label className="text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground mt-3 mb-1">
                Model
              </label>
              <Select
                value={state.selected_model || cfg.selected_model}
                onValueChange={(v) => setField(cfg.provider_id, "selected_model", v)}
              >
                <SelectTrigger
                  className="rounded-lg h-9 text-xs"
                  data-testid={`llm-model-${cfg.provider_id}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {cfg.models.map((m) => (
                    <SelectItem key={m} value={m} className="text-xs cs-mono">
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex gap-2 mt-4">
                <Button
                  onClick={() => save(cfg)}
                  disabled={isSaving}
                  className="flex-1 bg-[#00579e] hover:bg-[#004580] text-white rounded-full h-9 text-xs"
                  data-testid={`llm-save-${cfg.provider_id}`}
                >
                  {isSaving ? (
                    <Loader2 className="w-3.5 h-3.5 cs-spin" />
                  ) : (
                    <>
                      <KeyRound className="w-3.5 h-3.5 mr-1.5" />
                      {cfg.configured ? "Update key" : "Save key"}
                    </>
                  )}
                </Button>
                {cfg.configured && (
                  <Button
                    variant="outline"
                    onClick={() => setRemoveTarget(cfg)}
                    className="rounded-full h-9 w-9 p-0"
                    aria-label="Remove"
                    data-testid={`llm-remove-${cfg.provider_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-[#ef4444]" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <AlertDialog open={!!removeTarget} onOpenChange={(v) => !v && setRemoveTarget(null)}>
        <AlertDialogContent data-testid="llm-remove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeTarget?.name} key?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the stored API key. AI features that depend on{" "}
              {removeTarget?.name} will fall back to the Emergent LLM key.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="llm-remove-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => removeTarget && remove(removeTarget)}
              data-testid="llm-remove-confirm-button"
              className="bg-[#ef4444] hover:bg-[#dc2626]"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
